import type { LocationQuery } from "vue-router";
import { useRootStore } from "@/store/root";
import router from "@/router";
import { postMessageWrapper } from "@/utils/iframeCommunication";
import { firstQueryValue } from "@/utils/brokerUrlQuery";

export const COVOMO_BROKER_AFFILIATE_STATE_TYPE = "covomoBrokerAffiliateState";

function asAffiliateString(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v).trim();
  return "";
}

/**
 * Snapshot of broker/pool fields, current `a` / `r` (URL + store flags + affiliateData slice)
 * for the embedding parent (`postMessage`).
 */
export function buildCovomoBrokerAffiliateStatePayload(
  rootStore: ReturnType<typeof useRootStore>,
  routeQuery: LocationQuery,
) {
  const ad = (rootStore.affiliateData || {}) as Record<string, unknown>;
  return {
    type: COVOMO_BROKER_AFFILIATE_STATE_TYPE,
    broker: {
      broker_phone: rootStore.brokerPhone ?? "",
      broker_name: rootStore.brokerName ?? "",
      broker_company: rootStore.brokerCompany ?? "",
      broker_email: rootStore.brokerEmail ?? "",
      pool_email: rootStore.poolEmail ?? "",
      pool_name: rootStore.poolName ?? "",
      broker_id: rootStore.brokerId ?? "",
      broker_sub_id: rootStore.brokerSubId ?? "",
      broker_info_prefill: rootStore.brokerInfoPrefill ?? "",
      broker_info_visible: rootStore.brokerInfovisible ?? "",
    },
    /** Short aliases (same values as `broker` where applicable). */
    aliases: {
      vmtel: rootStore.brokerPhone ?? "",
      vmname: rootStore.brokerName ?? "",
      company: rootStore.brokerCompany ?? "",
      vmmail: rootStore.brokerEmail ?? "",
      poolmail: rootStore.poolEmail ?? "",
      poolname: rootStore.poolName ?? "",
      vmnr: rootStore.brokerId ?? "",
      vmunr: rootStore.brokerSubId ?? "",
    },
    query: {
      a: firstQueryValue(routeQuery, "a"),
      r: firstQueryValue(routeQuery, "r"),
    },
    flags: {
      hasAParameter: Boolean(rootStore.hasAParameter),
      hasRParameter: Boolean(rootStore.hasRParameter),
    },
    affiliateData: {
      affiliate_id: asAffiliateString(ad.affiliate_id),
      a: asAffiliateString(ad.a),
      r: asAffiliateString(ad.r),
      source: asAffiliateString(ad.source),
    },
  };
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 48;

/**
 * Sends `covomoBrokerAffiliateState` to `window.parent` (debounced; skips when not embedded).
 */
export function schedulePostBrokerAffiliateStateToParent(): void {
  if (typeof window === "undefined") return;
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const rootStore = useRootStore();
    const routeQuery = router.currentRoute.value.query;
    const payload = buildCovomoBrokerAffiliateStatePayload(
      rootStore,
      routeQuery,
    );
    postMessageWrapper(payload, "*", true);
  }, DEBOUNCE_MS);
}
